<template>
	<i
		class="c-icon"
		:class="classes"
		:style="iconStyle"
		aria-hidden="true"
	>
		<svg
			v-if="svgPathData"
			data-testid="svg-icon"
			class="c-icon__svg"
			:viewBox="viewBox"
			alt=""
		>
			<title v-if="title">{{ title }}</title>
			<path
				class="c-icon__path"
				:d="svgPathData"
				v-bind="pathAttributes"
			/>
		</svg>
		<div
			data-testid="complex-icon"
			v-html="showComplexIcon"
			v-else
		/>
	</i>
</template>

<script>
/**
 * <c-icon src="@icons/file" />
 */
export default {
	name: 'c-icon',

	props: {
		src: { type: null },
		size: { type: String },
		title: { type: String },
		hasBorder: { type: Boolean, default: false },
		circleMode: { type: Boolean, default: false },
		autoSize: { type: Boolean, default: false },
	},

	computed: {
		data({ src }) {
			return src.icon || src.definition?.icon || src.default?.icon;
		},

		width({ data: [width] }) {
			return width;
		},

		height({ data: [, height] }) {
			return height;
		},

		viewBox({ width, height }) {
			return `0 0 ${width} ${height}`;
		},

		svgPathData({ data: [, , svgPathData] }) {
			return svgPathData;
		},

		pathAttributes({ data: [, , , attributes] }) {
			return attributes ? Object.fromEntries(attributes) : { fill: 'currentColor' };
		},

		classes({ size, hasBorder, circleMode, autoSize }) {
			const sizeClass = size ? `--${size}-size` : '';
			const hasBorderClass = hasBorder ? `--has-border` : '';
			const circleModeClass = circleMode ? `--circle-mode` : '';
			const autoSizeClass = autoSize ? `--auto-size` : '';
			return `${sizeClass} ${hasBorderClass} ${circleModeClass} ${autoSizeClass}`;
		},

		scaleStyle({ data }) {
			if (!data) return {};
			const [width, height] = data;
			const viewBoxSize = Math.max(width, height);
			const referenceSize = 46;
			if (viewBoxSize >= referenceSize) return {};
			const scale = Math.sqrt(viewBoxSize / referenceSize);
			return { '--icon-scale': scale };
		},

		circleStyle({ circleMode, scaleStyle }) {
			return circleMode ? scaleStyle : {};
		},

		autoSizeStyle({ autoSize, scaleStyle }) {
			return autoSize ? scaleStyle : {};
		},

		iconStyle({ circleStyle, autoSizeStyle }) {
			return { ...circleStyle, ...autoSizeStyle };
		},

		showComplexIcon({ src }) {
			const { icon } = src;
			const svg = icon[3];

			return svg;
		},
	},

	mounted() {
		if (this.circleMode) {
			this.$nextTick(() => this.centerSvgInCircle());
		}
	},

	updated() {
		if (this.circleMode) {
			this.$nextTick(() => this.centerSvgInCircle());
		}
	},

	methods: {
		centerSvgInCircle() {
			if (!this.circleMode) return;

			const el = this.$el;
			if (!el) return;

			const svg = el.querySelector('svg');
			if (!svg) return;

			try {
				svg.querySelectorAll('path[d]').forEach((p) => p.removeAttribute('transform'));

				let paths;
				if (this.svgPathData) {
					paths = svg.querySelectorAll('path[d]');
				} else {
					paths = Array.from(svg.querySelectorAll('path[d]')).filter((p) => {
						let parent = p.parentElement;
						while (parent && parent !== svg) {
							if (['mask', 'clipPath'].includes(parent.tagName)) return false;
							parent = parent.parentElement;
						}
						return true;
					});
				}

				if (!paths.length) return;

				let minX = Infinity,
					minY = Infinity,
					maxX = -Infinity,
					maxY = -Infinity;
				paths.forEach((p) => {
					const bbox = p.getBBox();
					minX = Math.min(minX, bbox.x);
					minY = Math.min(minY, bbox.y);
					maxX = Math.max(maxX, bbox.x + bbox.width);
					maxY = Math.max(maxY, bbox.y + bbox.height);
				});

				const contentCenterX = minX + (maxX - minX) / 2;
				const contentCenterY = minY + (maxY - minY) / 2;
				const viewBox = svg.viewBox.baseVal;
				const viewBoxCenterX = viewBox.x + viewBox.width / 2;
				const viewBoxCenterY = viewBox.y + viewBox.height / 2;
				const threshold = 0.5;

				if (this.svgPathData) {
					const dx = viewBoxCenterX - contentCenterX;
					const dy = viewBoxCenterY - contentCenterY;
					if (Math.abs(dx) > threshold || Math.abs(dy) > threshold) {
						paths.forEach((p) => p.setAttribute('transform', `translate(${dx}, ${dy})`));
					}
				} else {
					const dvx = contentCenterX - viewBoxCenterX;
					const dvy = contentCenterY - viewBoxCenterY;
					if (Math.abs(dvx) > threshold || Math.abs(dvy) > threshold) {
						svg.setAttribute(
							'viewBox',
							`${viewBox.x + dvx} ${viewBox.y + dvy} ${viewBox.width} ${viewBox.height}`
						);
					}
				}
			} catch {
				// silently ignore if bbox is unavailable
			}
		},
	},
};
</script>

<style lang="scss" scoped>
.c-icon {
	display: inline-block;
	width: 1em;
	height: 1em;
	overflow: visible;
	flex-shrink: 0;
}

.c-icon.--has-border {
	border: 1px solid;
	border-radius: var(--border-radius-s);
	border-color: var(--color-secondary);
}

.c-icon__svg {
	display: block;
}

.c-icon.--xs-size {
	@extend %typo-xs-light;
}

.c-icon.--inherit-size {
	font-size: inherit;
}

.c-icon.--s-size {
	@extend %typo-s-light;
}

.c-icon.--m-size {
	@extend %typo-m-light;
}

.c-icon.--l-size {
	@extend %typo-l-light;
}

.c-icon.--xl-size {
	@extend %typo-xl-light;
}

.c-icon.--xxl-size {
	@extend %typo-xxl-light;
}

.c-icon.--xxxl-size {
	@extend %typo-xxxl-light;
}

.c-icon.--auto-size {
	.c-icon__svg,
	[data-testid='complex-icon'] svg {
		width: 100%;
		height: 100%;
		transform: scale(var(--icon-scale, 1));
		transform-origin: center center;
	}
}

.c-icon.--circle-mode {
	background: RGB(var(--color-secondary));
	border-radius: var(--border-radius-full);
	width: 38px;
	height: 38px;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
	color: #fff;

	.c-icon__svg,
	[data-testid='complex-icon'] svg {
		width: 100%;
		height: 100%;
		transform: scale(var(--icon-scale, 1));
		transform-origin: center center;
	}
}
</style>
